<template>
  <div>
    <generic-table
      ref="datatable"
      :title="$t('identity verifications')"
      :columns="columns"
      :column_search="true"
      resource_url="/identity/verification/datatables"
    >
      <template v-slot:buttons>
        <button class="button is-secondary is-rounded" @click="create">
          <span class="icon"><i class="fa fa-plus"></i></span>
          <span>{{ $t("create") }}</span>
        </button>
      </template>
    </generic-table>
    <verification-modal ref="verification_modal" @success="refresh" />
    <detail-modal ref="detail_modal" />
  </div>
</template>

<script>
import GenericTable from "@/components/generic_table/GenericTable.vue";
import {
  generate_datatables_buttons,
  confirm_deletion_dialog,
  del,
  post,
  error_message,
  success_message,
  format_date_time,
} from "@/utils";
import VerificationModal from "../verification_modal/Index.vue";
import DetailModal from "../detail_modal/Index.vue";
import { truncate } from "lodash";

export default {
  name: "IdentityVerificationDatatable",
  components: { GenericTable, VerificationModal, DetailModal },
  data() {
    let v = this;
    return {
      columns: [
        {
          title: v.$t("date"),
          searchable: false,
          // width: "25%",
          data: (row) => {
            return format_date_time(row.create_date);
          },
        },
        {
          title: v.$t("name"),
          searchable: false,
          // width: "30%",
          data: (row) => {
            return `<div class="title is-6">
                      <span class="icon"><i class="fa fa-user"></i></span>
                      ${row.first_name} ${row.last_name}
                    </div>
                    <div class="subtitle is-7">
                      <span class="icon"><i class="fa fa-envelope"></i></span>
                      ${row.id_number || ""}
                    </div>`;
            // return `${row.first_name} ${row.last_name}`;
          },
        },
        {
          title: v.$t("flags"),
          searchable: false,
          width: "40%",
          data: (row) => {
            let renap_icon = row.renap_request_id
              ? `<span class="icon has-text-success"><i class="fa fa-check"></i></span>`
              : `<span class="icon has-text-warning"><i class="fa fa-exclamation-triangle"></i></span>`;
            let renap_tag = `<span class="tag">
                              ${renap_icon}
                              ${v.$t("renap data")}
                            </span>`;

            let selfie_icon = row.has_selfie
              ? `<span class="icon has-text-success"><i class="fa fa-check"></i></span>`
              : `<span class="icon has-text-warning"><i class="fa fa-exclamation-triangle"></i></span>`;
            let selfie_tag = `<span class="tag">
                              ${selfie_icon}
                              ${v.$t("selfie")}
                            </span>`;

            let id_front_icon = row.has_id_front
              ? `<span class="icon has-text-success"><i class="fa fa-check"></i></span>`
              : `<span class="icon has-text-warning"><i class="fa fa-exclamation-triangle"></i></span>`;
            let id_front_tag = `<span class="tag">
                              ${id_front_icon}
                              ${v.$t("id front")}
                            </span>`;

            let id_back_icon = row.has_id_back
              ? `<span class="icon has-text-success"><i class="fa fa-check"></i></span>`
              : `<span class="icon has-text-warning"><i class="fa fa-exclamation-triangle"></i></span>`;
            let id_back_tag = `<span class="tag">
                              ${id_back_icon}
                              ${v.$t("id back")}
                            </span>`;

            let face_analysis_icon = row.has_face_analysis
              ? `<span class="icon has-text-success"><i class="fa fa-check"></i></span>`
              : `<span class="icon has-text-warning"><i class="fa fa-exclamation-triangle"></i></span>`;
            let face_analysis_tag = `<span class="tag">
                              ${face_analysis_icon}
                              ${v.$t("face analysis")}
                            </span>`;
            let id_front_analysis_icon = row.has_id_front_analysis
              ? `<span class="icon has-text-success"><i class="fa fa-check"></i></span>`
              : `<span class="icon has-text-warning"><i class="fa fa-exclamation-triangle"></i></span>`;
            let id_front_analysis_tag = `<span class="tag">
                              ${id_front_analysis_icon}
                              ${v.$t("id front analysis")}
                            </span>`;
            let id_back_analysis_icon = row.has_id_back_analysis
              ? `<span class="icon has-text-success"><i class="fa fa-check"></i></span>`
              : `<span class="icon has-text-warning"><i class="fa fa-exclamation-triangle"></i></span>`;
            let id_back_analysis_tag = `<span class="tag">
                              ${id_back_analysis_icon}
                              ${v.$t("id back analysis")}
                            </span>`;

            return `<div class="tags">${renap_tag}${selfie_tag}${id_front_tag}${id_back_tag}${face_analysis_tag}${id_front_analysis_tag}${id_back_analysis_tag}</div>`;
          },
        },
        // {
        //   title: v.$t("data"),
        //   searchable: false,
        //   width: "55%",
        //   data: (row) => {
        //     // return truncate(JSON.stringify(row), { length: 65 });
        //     return JSON.stringify(row, null, 2);
        //   },
        // },
        {
          title: v.$t("options"),
          width: "25%",
          searchable: false,
          data: (row) => {
            return generate_datatables_buttons([
              {
                args: { uuid: row.uuid },
                tooltip: v.$t("view"),
                icon: "fa fa-eye",
                icon_class: "is-small",
                click: function (e) {
                  // console.log("edit order", e.detail.uuid);
                  v.$refs.detail_modal.show_modal(e.detail.uuid);
                },
              },
              {
                args: { uuid: row.uuid },
                tooltip: v.$t("start analysis"),
                icon: "fa fa-angle-right",
                icon_class: "is-small has-text-danger",
                click: function (e) {
                  v.$root.loader(true);
                  post("/identity/verification/analyze", { uuid: row.uuid })
                    .then(() => {
                      success_message(
                        v.$t("verification analysis started successfully")
                      );
                      v.refresh();
                    })
                    .catch((error) => {
                      error_message(error);
                    })
                    .finally(() => {
                      v.$root.loader(false);
                    });
                },
              },
              {
                args: { uuid: row.uuid },
                tooltip: v.$t("get renap info"),
                icon: "fa fa-angle-right",
                icon_class: "is-small has-text-warning",
                click: function (e) {
                  v.$root.loader(true);
                  post("/identity/verification/renap", { uuid: row.uuid })
                    .then(() => {
                      v.refresh();
                    })
                    .catch((error) => {
                      error_message(error);
                    })
                    .finally(() => {
                      v.$root.loader(false);
                    });
                },
              },
              {
                args: { uuid: row.uuid },
                tooltip: v.$t("start verification"),
                icon: "fa fa-angle-right",
                icon_class: "is-small has-text-info",
                click: function (e) {
                  // console.log("edit order", e.detail.uuid);
                  // v.$refs.detail_modal.show_modal(e.detail.uuid);
                  window.open(`/identity/perform/${e.detail.uuid}`, "_blank");
                  // v.$router.push({
                  //   path: `/identity/perform/${e.detail.uuid}`,
                  // });
                },
              },
              {
                args: { uuid: row.uuid },
                tooltip: v.$t("delete"),
                icon: "fa fa-trash",
                icon_class: "is-small has-text-danger",
                click: (e) => {
                  let uuid = e.detail.uuid;
                  confirm_deletion_dialog(
                    v.$t("are you sure to delete identity verification?"),
                    v,
                    () => {
                      del("/identity/verification", { uuid: uuid })
                        .then(() => {
                          v.refresh();
                        })
                        .catch((error) => {
                          error_message(error);
                        });
                    }
                  );
                },
              },
            ]);
          },
        },
      ],
    };
  },
  methods: {
    // format_date_time,
    create() {
      let v = this;
      v.$refs.verification_modal.show_modal();
    },
    refresh() {
      let v = this;
      v.$refs.datatable.refresh();
    },
  },
  mounted() {
    this.refresh();
  },
};
</script>

<style></style>
